/**
 * Convert a parsed chart difficulty (game-engine schema: taps/rails/flicks with
 * Beat in 64-tick units, normalized Position, and a seconds Time) into flat
 * playable notes the gameplay loop can schedule.
 */
/** One node of a trail's path: a hit time + the lane it passes through. A
 *  straight trail has 2 (head + tail); a curving trail has the rail's waypoints
 *  so the ribbon snakes across lanes like in the editor. */
export interface TrailPoint { timeSec: number; lane: number; }

export interface PlayNote {
  id: string;
  lane: number;          // 0..laneCount-1 (trail head lane, for hit detection)
  timeSec: number;       // hit time in audio seconds
  kind: 'tap' | 'trail';
  endSec: number;        // === timeSec for taps; tail end for trails
  /** Trail path (≥2 points). Only present for kind === 'trail'. */
  points?: TrailPoint[];
  // runtime judging state (mutated by the game loop)
  judged?: 'perfect' | 'good' | 'miss';
  held?: boolean;        // trail currently being held
}

const TICKS_PER_BEAT = 64;
const LANE_COUNT = 4;

/** The lane count this difficulty was authored at (3 or 4). Older charts have no
 *  field → 4. Position fields are normalized against THIS count. */
export function laneCountOfDifficulty(diff: any): number {
  return diff?.laneCount === 3 ? 3 : 4;
}

function laneOf(position: number, laneCount: number): number {
  return Math.max(0, Math.min(laneCount - 1, Math.floor(position * laneCount)));
}

export function notesFromDifficulty(chart: any, diff: any): PlayNote[] {
  const bpm = chart?.bpm || 120;
  const offset = chart?.startOffset || 0;
  const spb = 60 / bpm;
  const lc = laneCountOfDifficulty(diff);
  const toSec = (beatTicks: number) => offset + (beatTicks / TICKS_PER_BEAT) * spb;

  const out: PlayNote[] = [];
  let n = 0;

  for (const t of diff?.taps || []) {
    const time = Number.isFinite(t.Time) ? t.Time : toSec(t.Beat);
    out.push({ id: t.Id || `t${n++}`, lane: laneOf(t.Position, lc), timeSec: time, endSec: time, kind: 'tap' });
  }
  // Flicks (directional) play as taps in their lane for now.
  for (const f of diff?.flicks || []) {
    const time = Number.isFinite(f.Time) ? f.Time : toSec(f.Beat);
    out.push({ id: f.Id || `f${n++}`, lane: laneOf(f.Position, lc), timeSec: time, endSec: time, kind: 'tap' });
  }
  for (const r of diff?.rails || []) {
    const start = Number.isFinite(r.Time) ? r.Time : toSec(r.Beat);
    let end: number;
    if (Number.isFinite(r.EndTime)) end = r.EndTime;
    else if (Number.isFinite(r.Duration)) end = toSec(r.Beat + r.Duration);
    else end = start + 0.3;
    end = Math.max(end, start + 0.12);

    // Path through the rail's waypoints (curving trails change lane). Fall back
    // to a straight head→tail line when no waypoints are present.
    let points: TrailPoint[];
    if (Array.isArray(r.Waypoints) && r.Waypoints.length >= 2) {
      points = r.Waypoints.map((w: any) => ({
        timeSec: Number.isFinite(w.Time) ? w.Time : toSec(w.Beat),
        lane: laneOf(w.Position, lc),
      })).sort((a: TrailPoint, b: TrailPoint) => a.timeSec - b.timeSec);
    } else {
      const endLane = laneOf(Number.isFinite(r.EndPosition) ? r.EndPosition : r.Position, lc);
      points = [{ timeSec: start, lane: laneOf(r.Position, lc) }, { timeSec: end, lane: endLane }];
    }

    out.push({
      id: r.Id || `r${n++}`,
      lane: points[0].lane,
      timeSec: points[0].timeSec,
      endSec: points[points.length - 1].timeSec,
      kind: 'trail',
      points,
    });
  }

  return out.sort((a, b) => a.timeSec - b.timeSec);
}

export const PLAY_LANE_COUNT = LANE_COUNT;
